import React, { useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { headRewrite } from "../function/headRewrite";
import { COLLECTIONS } from "../content/archiveCollection";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
  },
  previewSwitch: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "rgba(238, 238, 238, 0.96)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #c8c8c8",
  },
  previewSwitchInner: {
    minHeight: 74,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 0),
    },
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  previewSubTitle: {
    fontSize: 14,
    color: "#333",
  },
  previewButtons: {
    display: "flex",
    gap: theme.spacing(1.2),
    flexWrap: "wrap",
  },
  previewButton: {
    borderRadius: 0,
    border: "1px solid #111",
    boxShadow: "none",
    padding: theme.spacing(1.1, 2.2),
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.04em",
    "&:hover": {
      boxShadow: "none",
    },
  },
  previewButtonActive: {
    background: "#111",
    color: "#eeeeee",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  previewButtonInactive: {
    background: "transparent",
    color: "#111",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.7vw, 54px)",
    lineHeight: 1.22,
    letterSpacing: "-0.07em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.6vw, 32px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 650,
    fontSize: 15,
    lineHeight: 2.05,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 360,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 20,
    width: 260,
    height: 260,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 20,
    top: 48,
    width: 180,
    height: 180,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  imageMock: {
    position: "relative",
    width: "72%",
    maxWidth: 390,
    minHeight: 420,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "82%",
      minHeight: 290,
      maxWidth: 300,
    },
  },
  imageMockImage: {
    width: "100%",
    height: "100%",
    minHeight: 450,
    objectFit: "cover",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  imageMockText: {
    fontSize: 12,
    lineHeight: 2,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#333",
  },
  thesisCard: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
    minHeight: 280,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.7,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  card: {
    border: "1px solid #111",
    minHeight: 300,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 240,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  archiveList: {
    borderTop: "1px solid #111",
  },
  archiveListItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  archiveListIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  archiveListTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.7,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  experienceBox: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
  },
  experienceItem: {
    borderTop: "1px solid #c8c8c8",
    padding: theme.spacing(2.2, 0),
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    fontSize: 15,
    lineHeight: 1.8,
    "&:first-child": {
      borderTop: "none",
      paddingTop: 0,
    },
  },
  experienceArrow: {
    fontWeight: 700,
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  imagePanel: {
    minHeight: 360,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(2.5),
  },
  imagePanelLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  imagePanelImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    border: "1px solid #111",
    marginBottom: theme.spacing(2.5),
    display: "block",
    background: "#eeeeee",
  },
  imagePanelLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  imagePanelTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.45,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  imagePanelText: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
  },
  audienceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  audienceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.5),
  },
  audienceTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: theme.spacing(1.2),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));


const audiences = [
  ["愛好家", "ブランドの背景まで深く知りたい人"],
  ["スタイリスト", "作品づくりのために実物を探す人"],
  ["コレクター", "所有と体験の両方から服を見る人"],
  ["学生", "モードを学ぶ入口を探している人"],
  ["海外層", "日本のアーカイブ視点に触れたい人"],
];

const buildImageUrl = (path) => `${process.env.REACT_APP_IMAGE_URL || ""}${path}`;

function CollectionSeo({ collection }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${collection.canonicalPath}`;
  const ogImage = collection.heroImage
    ? buildImageUrl(collection.heroImage)
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(collection.metaTitle, collection.metaDescription, collection.metaKeywords);
  }, [collection.metaTitle, collection.metaDescription, collection.metaKeywords]);

  return (
    <Helmet>
      <title>{collection.metaTitle}</title>
      <meta name="description" content={collection.metaDescription} />
      <meta name="keywords" content={collection.metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={collection.metaTitle} />
      <meta property="og:description" content={collection.metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: collection.metaTitle,
          description: collection.metaDescription,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "COMME des GARÇONS HOMME PLUS",
            collection.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Collection",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: collection.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
    </Helmet>
  );
}

function ArchiveFocusCard({ item, classes }) {
  const content = (
    <div>
      {item.productImage && (
        <img
          src={buildImageUrl(item.productImage)}
          alt={item.title}
          className={classes.imagePanelImage}
        />
      )}
      <div className={classes.imagePanelLabel}>{item.label}</div>
      <Typography component="h3" className={classes.imagePanelTitle}>
        {item.title}
      </Typography>
      <Typography component="p" className={classes.imagePanelText}>
        {item.text}
      </Typography>
    </div>
  );

  if (item.productPath) {
    return (
      <a
        href={item.productPath}
        className={`${classes.imagePanel} ${classes.imagePanelLink}`}
        aria-label={`${item.title}の商品ページを見る`}
      >
        {content}
      </a>
    );
  }

  return <div className={classes.imagePanel}>{content}</div>;
}

function CollectionArchivePage({ collection }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <CollectionSeo collection={collection} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{collection.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {collection.title}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {collection.lead}
                </Typography>
                <div className={classes.keywordLine}>
                  {collection.keywordLine.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < collection.keywordLine.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
                <div className={classes.buttonRow} style={{ marginTop: 32 }}>
                  <Button className={classes.primaryButton} component="a" href="https://www.enclopedia-official.com/">
                    レンタルできるアイテムを見る
                  </Button>
                  <a href="/help_guide" className={classes.textLink}>
                    Enclopediaの使い方
                  </a>
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{collection.heroNumber}</div>
                  <div className={classes.imageMock}>
                    <img
                      src={buildImageUrl(collection.heroImage)}
                      alt={collection.heroImageAlt}
                      className={classes.imageMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>02 / WHY IT MATTERS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.whyTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.whyBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {collection.thesis}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    <span>knowledge</span>
                    <span>/</span>
                    <span>collection</span>
                    <span>/</span>
                    <span>experience</span>
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>03 / THREE POINTS</span>
              <span className={classes.labelLine} />
            </div>
            <Grid container spacing={3}>
              {collection.pillars.map((pillar) => (
                <Grid item xs={12} md={4} key={pillar.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{pillar.number}</div>
                      <div className={classes.cardTitle}>{pillar.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {pillar.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>04 / HOW TO READ</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.readingTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.readingIntro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.archiveList}>
                  {collection.observations.map((item) => (
                    <div className={classes.archiveListItem} key={item.index}>
                      <div className={classes.archiveListIndex}>{item.index}</div>
                      <div className={classes.archiveListTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {collection.quote}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>05 / ENCLOPEDIA PERSPECTIVE</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.perspectiveTitle}
            </Typography>
            <Grid container spacing={3}>
              {collection.perspectiveCards.map((card) => (
                <Grid item xs={12} md={4} key={card.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{card.number}</div>
                      <div className={classes.cardTitle}>{card.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {card.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>06 / EXPERIENCE</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.experienceTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.experienceBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.experienceBox}>
                  {collection.experienceItems.map((item) => (
                    <div className={classes.experienceItem} key={item}>
                      <span>{item}</span>
                      <span className={classes.experienceArrow}>→</span>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>07 / ARCHIVE FOCUS</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.archiveItemsTitle}
            </Typography>
            <Typography component="p" className={classes.heroBody}>
              {collection.archiveItemsBody}
            </Typography>
            <div className={classes.imageGrid}>
              {collection.archiveItems.map((item) => (
                <ArchiveFocusCard item={item} classes={classes} key={item.label} />
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>08 / AUDIENCE</span>
              <span className={classes.labelLine} />
            </div>
            <div className={classes.audienceGrid}>
              {audiences.map(([title, text]) => (
                <div className={classes.audienceItem} key={title}>
                  <div className={classes.audienceTitle}>{title}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="p" className={classes.finalTitle}>
              アーカイブを、読むだけで終わらせない。
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              コレクションの背景を知り、実物に触れ、服を体験として理解するための入口です。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/">
              アイテムを探す
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveCollectionIndex() {
  const classes = useStyles();
  const collections = useMemo(() => Object.values(COLLECTIONS), []);

  useEffect(() => {
    headRewrite(
      "Archive Collection Study｜Enclopedia",
      "Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。",
      "archive collection,enclopedia,アーカイブファッション,コレクション解説"
    );
  }, []);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Archive Collection Study｜Enclopedia</title>
        <meta
          name="description"
          content="Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。"
        />
        <link
          rel="canonical"
          href={`${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`}
        />
      </Helmet>
      <Container maxWidth="xl">
        <section className={classes.hero}>
          <div className={classes.labelRow}>
            <span>ARCHIVE COLLECTION STUDY</span>
            <span className={classes.labelLine} />
          </div>
          <Typography variant="h1" component="h1" className={classes.heroTitle}>
            コレクション解説
          </Typography>
          <Typography component="p" className={classes.heroBody}>
            ブランドの各コレクションを、背景・ルック・素材・体験導線から読み解くためのページです。
          </Typography>
        </section>
        <section className={classes.section}>
          <div className={classes.archiveList}>
            {collections.map((collection, index) => (
              <a
                href={collection.canonicalPath}
                className={classes.archiveListItem}
                key={collection.canonicalPath}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <div className={classes.archiveListIndex}>
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className={classes.archiveListTitle}>{collection.title}</div>
                <Typography component="p" className={classes.noteBody}>
                  {collection.metaDescription}
                </Typography>
              </a>
            ))}
          </div>
        </section>
      </Container>
    </div>
  );
}

const findCollectionBySlug = (slug) => {
  if (!slug) return null;
  return Object.values(COLLECTIONS).find((collection) => {
    const canonicalSlug = collection.canonicalPath.split("/").filter(Boolean).pop();
    return collection.slug === slug || canonicalSlug === slug;
  });
};

export default function ArchiveCollectionScreen() {
  const { slug } = useParams();
  const collection = useMemo(() => findCollectionBySlug(slug), [slug]);

  if (!slug) {
    return <ArchiveCollectionIndex />;
  }

  if (!collection) {
    return (
      <div style={{ padding: 48 }}>
        <Helmet>
          <title>Archive Collection Not Found｜Enclopedia</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Typography component="h1" variant="h1">
          Archive collection not found
        </Typography>
      </div>
    );
  }

  return <CollectionArchivePage collection={collection} />;
}
